// Chrome refactor — the single switch point where the chrome's token sets diverge
// by surface. AppShell/Sidebar/TopBar read classes from here instead of hardcoding
// parchment/ink, so the studio shell flips dark purely by selecting the `video`
// set. The active-accent stays oxblood on both surfaces (the surface picks the
// shade: base oxblood on editorial, the brighter oxblood-live on video).

export type Surface = 'editorial' | 'video';

export interface ChromeTokens {
  /** AppShell <main> canvas background. */
  shellMain: string;
  sidebar: {
    /** border-color + bg on the <aside> (the `border-r` side stays in the component). */
    aside: string;
    collapseBtn: string;
    sectionLabel: string;
    /** the footer role badge under the display name (Spec 27) */
    roleBadge: string;
    /** inactive item text + hover */
    item: string;
    /** active item tint + text */
    itemActive: string;
    /** the oxblood left-bar accent on the active item */
    itemBar: string;
    /** no-avatar placeholder circle */
    avatarFallback: string;
    footerBorder: string;
    footerLink: string;
    footerSubLink: string;
  };
  topbar: {
    /** border-color + bg on the <header> (the `border-b` side stays in the component). */
    header: string;
    title: string;
    menuBtn: string;
  };
}

const editorial: ChromeTokens = {
  shellMain: '',
  sidebar: {
    aside: 'border-ink/10 bg-parchment',
    collapseBtn: 'text-ink/50 hover:bg-ink/5 hover:text-ink',
    sectionLabel: 'text-ink/40',
    roleBadge: 'text-oxblood/80',
    item: 'text-ink/80 hover:bg-ink/5 hover:text-ink',
    itemActive: 'bg-oxblood/10 text-oxblood',
    itemBar: 'bg-oxblood',
    avatarFallback: 'bg-ink/10 text-ink/60',
    footerBorder: 'border-ink/10',
    footerLink: 'text-ink/80 hover:bg-ink/5',
    footerSubLink: 'text-ink/60 hover:bg-ink/5',
  },
  topbar: {
    header: 'border-ink/10 bg-parchment/95',
    title: 'text-ink',
    menuBtn: 'text-ink/70 hover:bg-ink/5',
  },
};

// The dark "video" surface — the studio shell (/creator/studio + /admin/studio).
const video: ChromeTokens = {
  shellMain: 'bg-video-bg',
  sidebar: {
    aside: 'border-video-line bg-video-panel',
    collapseBtn: 'text-video-text-muted hover:bg-video-card hover:text-video-text',
    sectionLabel: 'text-video-text-faint',
    roleBadge: 'text-oxblood-live/90',
    item: 'text-video-text-muted hover:bg-video-card hover:text-video-text',
    itemActive: 'bg-oxblood-live/15 text-video-text',
    itemBar: 'bg-oxblood-live',
    avatarFallback: 'bg-video-card text-video-text-faint',
    footerBorder: 'border-video-line',
    footerLink: 'text-video-text-muted hover:bg-video-card',
    footerSubLink: 'text-video-text-faint hover:bg-video-card',
  },
  topbar: {
    header: 'border-video-line bg-video-bg/95',
    title: 'text-video-text',
    menuBtn: 'text-video-text-muted hover:bg-video-card',
  },
};

export const chromeTokens: Record<Surface, ChromeTokens> = { editorial, video };
